import type { MouseEvent } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ActionButton,
  BrandLoader,
  ColumnMasonry,
  type ColumnMasonryColumns,
} from '@core/components';
import type { Design } from '../../api/design';
import type { DesignPages } from '../hooks/useDesignPages';
import { getImageAspectRatio } from '../utils/image';
import DesignCard from './DesignCard';

/**
 * Card height estimates for column balancing only, in px at a typical column
 * width; real heights come from the image aspect ratio and content.
 */
const NOMINAL_COLUMN_WIDTH = 220;
const CARD_CONTENT_HEIGHT = 62;
const CHIP_ROW_HEIGHT = 28;

function estimateCompactCardHeight(design: Design): number {
  return (
    NOMINAL_COLUMN_WIDTH / (getImageAspectRatio(design) ?? 3 / 5) +
    CARD_CONTENT_HEIGHT
  );
}

function estimateCardHeight(design: Design): number {
  const chipRows = design.centerFinish ? 2 : 1;
  return estimateCompactCardHeight(design) + chipRows * CHIP_ROW_HEIGHT;
}

interface DesignGridProps {
  pages: DesignPages;
  columns: ColumnMasonryColumns;
  priorityCount: number; // Leading cards that load their image eagerly.
  emptyMessage: string;
  minHeight: number | string;
  selectedCode?: string;
  compact?: boolean; // Cards show only the design code.
  onOpen: (design: Design) => void;
  onMenu?: (event: MouseEvent<HTMLElement>, design: Design) => void;
}

const DesignGrid = ({
  pages,
  columns,
  priorityCount,
  emptyMessage,
  minHeight,
  selectedCode,
  compact,
  onOpen,
  onMenu,
}: DesignGridProps) => {
  const {
    designs,
    isPending,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    sentinelRef,
  } = pages;

  if (isPending || designs.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          minHeight,
        }}
      >
        {isPending ? (
          <BrandLoader />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {emptyMessage}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, minHeight }}>
      <ColumnMasonry
        items={designs}
        getKey={(design) => design.id}
        renderItem={(design, index) => (
          <DesignCard
            design={design}
            priority={index < priorityCount}
            selected={design.code === selectedCode}
            compact={compact}
            onOpen={onOpen}
            onMenu={onMenu}
          />
        )}
        columns={columns}
        spacing={2}
        estimateHeight={
          compact ? estimateCompactCardHeight : estimateCardHeight
        }
      />
      <Box ref={sentinelRef} sx={{ height: '1px' }} />
      {isFetchingNextPage ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <BrandLoader size={48} />
        </Box>
      ) : null}
      {isFetchNextPageError && !isFetchingNextPage ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Couldn&apos;t load more designs.
          </Typography>
          <ActionButton
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
          >
            Retry
          </ActionButton>
        </Box>
      ) : null}
    </Box>
  );
};

export default DesignGrid;
